'use client';

import { useTranslations } from 'next-intl';
import { useCallback, useEffect, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import Grid from '@/components/Grid';
import Section from '@/components/Section';
import EventTile from '@/components/ShowTile';
import type { Event_Entry } from '@/lib/graphql-sdk';
import { formatDate } from '@/utils/dateFormat';
import { filterEvents } from '@/utils/filterEvents';
import { getShowProgramStart } from '@/utils/show';
import FilteredShowsFilter from './FilteredShowsFilter';

interface Filter {
    value: string;
    label: string;
}

interface FilteredShowsMonthlyProps {
    shows: Event_Entry[];
    locale: string;
    activeFilters?: string[];
    filterAction?: (filter: string) => void;
}

interface ResultsByMonth {
    [key: string]: Event_Entry[];
}

const STICKY_TOP_OFFSET = 48; // matches sm:top-12 (3rem)

export default function FilteredShowsMonthly({
    shows,
    locale,
    activeFilters = [],
    filterAction = () => {},
}: FilteredShowsMonthlyProps): React.JSX.Element {
    const t = useTranslations('FilteredShows');
    const [resultsByMonth, setResultsByMonth] = useState<ResultsByMonth>({});
    const [isSticky, setIsSticky] = useState(false);
    const filterBarRef = useRef<HTMLDivElement>(null);

    const checkSticky = useCallback(() => {
        const el = filterBarRef.current;
        if (!el || typeof window === 'undefined') return;

        const rect = el.getBoundingClientRect();
        // Element is sticky when it's pinned at top-12 (48px)
        const sticky = rect.top <= STICKY_TOP_OFFSET + 4;
        setIsSticky(sticky);
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') return;

        checkSticky();
        window.addEventListener('scroll', checkSticky, { passive: true });
        window.addEventListener('resize', checkSticky);
        return () => {
            window.removeEventListener('scroll', checkSticky);
            window.removeEventListener('resize', checkSticky);
        };
    }, [checkSticky]);

    const availableFilters: Filter[] = [
        { value: 'english', label: t('only-english') },
        { value: 'best-of-stand-up', label: t('best-of-stand-up') },
        { value: 'open-mic', label: t('open-mic') },
        { value: 'double-act', label: t('double-act') },
        { value: 'solo-show', label: t('solo-show') },
    ];

    useEffect(() => {
        const results = filterEvents(shows, activeFilters).reduce(
            (eventsSoFar: ResultsByMonth, event: Event_Entry) => {
                const programStart = getShowProgramStart(event);
                const month = formatDate.monthLongWithYear(programStart, locale);
                const index = month ?? 'undefined';

                if (!eventsSoFar[index]) {
                    eventsSoFar[index] = [];
                }

                eventsSoFar[index].push(event);

                return eventsSoFar;
            },
            {}
        );

        setResultsByMonth(results ?? {});
    }, [shows, activeFilters, locale]);

    return (
        <>
            <div
                ref={filterBarRef}
                className={cn(
                    'relative z-30 bg-haug-ivory-30 sm:top-0 sm:sticky sm:pl-12 lg:ml-filterbar-start lg:mr-8',
                    'transition-[margin,padding] duration-300 ease-out',
                    isSticky &&
                        'sm:ml-navbar-start sm:pl-0 lg:ml-filterbar-start lg:pl-12'
                )}
            >
                <FilteredShowsFilter
                    availableFilters={availableFilters}
                    activeFilters={activeFilters}
                    filterAction={filterAction}
                />
            </div>

            {Object.keys(resultsByMonth).map((month) => (
                <div key={month}>
                    <Section title={month.split(' ')[0]} variant="collection">
                        {resultsByMonth[month].length ? (
                            <Grid>
                                {resultsByMonth[month].map((event, index) => (
                                    <EventTile
                                        key={event.id}
                                        event={event}
                                        priority={index < 4}
                                        className={'event-tile'}
                                        locale={locale}
                                    />
                                ))}
                            </Grid>
                        ) : (
                            <div>No results</div>
                        )}
                    </Section>
                </div>
            ))}
        </>
    );
}
